import {
  ABSENCE_TYPES,
  type AbsenceType,
  ACTIVITY_FILTERS,
  type ActivityFilter,
  CONTACT_SORTS,
  type ContactSort,
  DEAL_SORTS,
  DEAL_SOURCES,
  type DealSort,
  EQUIPMENT_SORTS,
  EQUIPMENT_WAITING,
  type EquipmentSort,
  type EquipmentWaiting,
  MODULES,
  REQUEST_TYPES,
  type RequestType,
  SAFETY_CATEGORIES,
  type SafetyCategory,
  TASK_SORTS,
  TASK_VIEWS,
  type TaskSort,
  type TaskView,
} from '@erp/shared';
import { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { AppShell } from './components/app-shell';
import { Spinner } from './components/ui';
import { ApiError } from './lib/api';
import { fetchMe, meQueryKey } from './lib/auth';
import { AdminAccessPage } from './pages/admin-access';
import { AdminMigrationPage } from './pages/admin-migration';
import { AdminRoutesPage } from './pages/admin-routes';
import { ApprovalsPage, type ApprovalsSearch } from './pages/approvals';
import {
  CompaniesPage,
  CompanyPage,
  ContactPage,
  ContactsPage,
  type ContactsSearch,
} from './pages/crm-clients';
import { DealPage } from './pages/crm-deal';
import { DealsPage, type DealsSearch } from './pages/crm-deals';
import {
  ActivitiesPage,
  type ActivitiesSearch,
  ChatsPage,
  type ChatsSearch,
  CrmSettingsPage,
  ProductsPage,
  type ProductsSearch,
} from './pages/crm-misc';
import { EmployeePage } from './pages/employee';
import { EmployeesPage } from './pages/employees';
import { EquipmentListPage, EquipmentPage, type EquipmentSearch } from './pages/equipment';
import { EquipmentSettingsPage } from './pages/equipment-settings';
import { type HelpdeskSearch, TicketPage, TicketsPage } from './pages/helpdesk';
import { HomePage } from './pages/home';
import { LoginPage } from './pages/login';
import {
  MealsMyPage,
  MealsPage,
  MealsPeoplePage,
  MealsReportPage,
  type MealsReportSearch,
  type MealsSearch,
  MealsSettingsPage,
} from './pages/meals';
import { NotFoundPage, NotificationsPage, PlannedModulePage, ProfilePage } from './pages/misc';
import { OrgPage } from './pages/org';
import { ProductionPage, type ProductionSearch } from './pages/production';
import { RequestPage } from './pages/request';
import { RequestsPage, type RequestsSearch } from './pages/requests';
import {
  SafetyEmployeePage,
  SafetyJournalPage,
  type SafetyJournalSearch,
  SafetyMyPage,
  SafetyPrintPage,
  type SafetyPrintSearch,
  type SafetySearch,
  SafetyStatusPage,
} from './pages/safety';
import { SafetySettingsPage } from './pages/safety-settings';
import { TaskPage } from './pages/task';
import { TASK_PRESETS, type TaskPreset, TasksPage, type TasksSearch } from './pages/tasks';
import { RecurringTasksPage } from './pages/tasks-recurring';
import { VacationPage } from './pages/vacation';
import { VacationsPage, type VacationsSearch } from './pages/vacations';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      refetchOnWindowFocus: false,
    },
  },
});

const rootRoute = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: Outlet,
  notFoundComponent: NotFoundPage,
});

async function currentUser(qc: QueryClient) {
  try {
    return await qc.ensureQueryData({ queryKey: meQueryKey, queryFn: fetchMe, staleTime: 60_000 });
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (s: Record<string, unknown>) => ({
    redirect: typeof s.redirect === 'string' ? s.redirect : undefined,
  }),
  beforeLoad: async ({ context }) => {
    if (await currentUser(context.queryClient)) throw redirect({ to: '/' });
  },
  component: LoginPage,
});

/** Всё, что требует входа, — внутри этого маршрута. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context, location }) => {
    const user = await currentUser(context.queryClient);
    if (!user) throw redirect({ to: '/login', search: { redirect: location.href } });
    return { user };
  },
  pendingComponent: () => <Spinner />,
  component: AppShell,
});

const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
const flag = (v: unknown) => (v === true || v === 'true' ? true : undefined);
const page = (v: unknown) => (Number(v) > 1 ? Number(v) : undefined);

const amount = (v: unknown) => {
  const n = Number(v);
  return v !== undefined && v !== '' && Number.isFinite(n) && n >= 0 ? n : undefined;
};
const isoDay = (v: unknown) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined);
const safetyCategory = (v: unknown) =>
  (SAFETY_CATEGORIES as readonly string[]).includes(String(v)) ? (v as SafetyCategory) : undefined;

/** Печать журнала — без меню и шапки, но только после входа. */
const safetyPrintRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/safety/print',
  beforeLoad: async ({ context, location }) => {
    if (!(await currentUser(context.queryClient)))
      throw redirect({ to: '/login', search: { redirect: location.href } });
  },
  validateSearch: (s: Record<string, unknown>): SafetyPrintSearch => ({
    category: safetyCategory(s.category),
    kind: str(s.kind),
    department: str(s.department),
    from: isoDay(s.from),
    to: isoDay(s.to),
  }),
  pendingComponent: () => <Spinner />,
  component: SafetyPrintPage,
});

const routes = [
  createRoute({ getParentRoute: () => appRoute, path: '/', component: HomePage }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/org',
    validateSearch: (s: Record<string, unknown>) => ({ department: str(s.department) }),
    component: OrgPage,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/employees',
    validateSearch: (s: Record<string, unknown>) => ({
      q: str(s.q),
      department: str(s.department),
      page: Number(s.page) > 1 ? Number(s.page) : undefined,
    }),
    component: EmployeesPage,
  }),
  createRoute({ getParentRoute: () => appRoute, path: '/employees/$id', component: EmployeePage }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/tasks',
    validateSearch: (s: Record<string, unknown>): TasksSearch => ({
      view: (TASK_VIEWS as readonly string[]).includes(String(s.view)) ? (s.view as TaskView) : undefined,
      mode: s.mode === 'list' || s.mode === 'deadline' ? s.mode : undefined,
      project: str(s.project),
      q: str(s.q),
      // Старые ссылки «?overdue=true» — это готовый фильтр «Просрочены»
      preset: (TASK_PRESETS as readonly string[]).includes(String(s.preset))
        ? (s.preset as TaskPreset)
        : s.overdue === true || s.overdue === 'true'
          ? 'overdue'
          : undefined,
      creator: str(s.creator),
      assignee: str(s.assignee),
      deadlineFrom: isoDay(s.deadlineFrom),
      deadlineTo: isoDay(s.deadlineTo),
      createdFrom: isoDay(s.createdFrom),
      createdTo: isoDay(s.createdTo),
      closedFrom: isoDay(s.closedFrom),
      closedTo: isoDay(s.closedTo),
      sort: (TASK_SORTS as readonly string[]).includes(String(s.sort)) ? (s.sort as TaskSort) : undefined,
      dir: s.dir === 'desc' ? 'desc' : s.dir === 'asc' ? 'asc' : undefined,
    }),
    component: TasksPage,
  }),
  createRoute({ getParentRoute: () => appRoute, path: '/tasks/recurring', component: RecurringTasksPage }),
  createRoute({ getParentRoute: () => appRoute, path: '/tasks/$id', component: TaskPage }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/approvals',
    validateSearch: (s: Record<string, unknown>): ApprovalsSearch => ({
      state: s.state === 'done' ? 'done' : undefined,
    }),
    component: ApprovalsPage,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/vacations',
    validateSearch: (s: Record<string, unknown>): VacationsSearch => ({
      tab: s.tab === 'mine' || s.tab === 'all' ? s.tab : undefined,
      month: typeof s.month === 'string' && /^\d{4}-\d{2}$/.test(s.month) ? s.month : undefined,
      department: str(s.department),
      type: (ABSENCE_TYPES as readonly string[]).includes(String(s.type))
        ? (s.type as AbsenceType)
        : undefined,
      person: str(s.person),
      from: isoDay(s.from),
      to: isoDay(s.to),
      kind: s.kind === 'annual' || s.kind === 'unpaid' ? s.kind : undefined,
      scale: s.scale === 'week' || s.scale === 'day' ? s.scale : undefined,
      day: isoDay(s.day),
    }),
    component: VacationsPage,
  }),
  createRoute({ getParentRoute: () => appRoute, path: '/vacations/$id', component: VacationPage }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/requests',
    validateSearch: (s: Record<string, unknown>): RequestsSearch => ({
      scope: s.scope === 'all' ? 'all' : undefined,
      type: (REQUEST_TYPES as readonly string[]).includes(String(s.type))
        ? (s.type as RequestType)
        : undefined,
      state: s.state === 'open' || s.state === 'closed' ? s.state : undefined,
      q: str(s.q),
      author: str(s.author),
      from: isoDay(s.from),
      to: isoDay(s.to),
    }),
    component: RequestsPage,
  }),
  createRoute({ getParentRoute: () => appRoute, path: '/requests/$id', component: RequestPage }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/crm',
    validateSearch: (s: Record<string, unknown>): DealsSearch => ({
      mode: s.mode === 'list' ? 'list' : undefined,
      scope: s.scope === 'mine' || s.scope === 'free' ? s.scope : undefined,
      direction: str(s.direction),
      state: s.state === 'won' || s.state === 'lost' || s.state === 'all' ? s.state : undefined,
      q: str(s.q),
      owner: str(s.owner),
      stage: str(s.stage),
      source: (DEAL_SOURCES as readonly string[]).includes(String(s.source)) ? String(s.source) : undefined,
      activity: (ACTIVITY_FILTERS as readonly string[]).includes(String(s.activity))
        ? (s.activity as ActivityFilter)
        : undefined,
      createdFrom: isoDay(s.createdFrom),
      createdTo: isoDay(s.createdTo),
      closedFrom: isoDay(s.closedFrom),
      closedTo: isoDay(s.closedTo),
      amountFrom: amount(s.amountFrom),
      amountTo: amount(s.amountTo),
      sort: (DEAL_SORTS as readonly string[]).includes(String(s.sort)) ? (s.sort as DealSort) : undefined,
      dir: s.dir === 'desc' ? 'desc' : s.dir === 'asc' ? 'asc' : undefined,
      page: page(s.page),
    }),
    component: DealsPage,
  }),
  createRoute({ getParentRoute: () => appRoute, path: '/crm/deals/$id', component: DealPage }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/crm/contacts',
    validateSearch: (s: Record<string, unknown>): ContactsSearch => ({
      q: str(s.q),
      mine: flag(s.mine),
      activity: (ACTIVITY_FILTERS as readonly string[]).includes(String(s.activity))
        ? (s.activity as ActivityFilter)
        : undefined,
      sort: (CONTACT_SORTS as readonly string[]).includes(String(s.sort))
        ? (s.sort as ContactSort)
        : undefined,
      dir: s.dir === 'desc' ? 'desc' : s.dir === 'asc' ? 'asc' : undefined,
      page: page(s.page),
    }),
    component: ContactsPage,
  }),
  createRoute({ getParentRoute: () => appRoute, path: '/crm/contacts/$id', component: ContactPage }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/crm/companies',
    validateSearch: (s: Record<string, unknown>): ContactsSearch => ({ q: str(s.q), page: page(s.page) }),
    component: CompaniesPage,
  }),
  createRoute({ getParentRoute: () => appRoute, path: '/crm/companies/$id', component: CompanyPage }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/crm/chats',
    validateSearch: (s: Record<string, unknown>): ChatsSearch => ({
      chat: str(s.chat),
      unread: flag(s.unread),
      q: str(s.q),
    }),
    component: ChatsPage,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/crm/activities',
    validateSearch: (s: Record<string, unknown>): ActivitiesSearch => ({
      state: s.state === 'done' ? 'done' : undefined,
      scope: s.scope === 'all' ? 'all' : undefined,
    }),
    component: ActivitiesPage,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/crm/products',
    validateSearch: (s: Record<string, unknown>): ProductsSearch => ({
      q: str(s.q),
      page: page(s.page),
      inactive: flag(s.inactive),
    }),
    component: ProductsPage,
  }),
  createRoute({ getParentRoute: () => appRoute, path: '/crm/settings', component: CrmSettingsPage }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/production',
    validateSearch: (s: Record<string, unknown>): ProductionSearch => ({ q: str(s.q) }),
    component: ProductionPage,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/equipment',
    validateSearch: (s: Record<string, unknown>): EquipmentSearch => ({
      lifecycle:
        s.lifecycle === 'production' || s.lifecycle === 'decommissioned' || s.lifecycle === 'all'
          ? s.lifecycle
          : undefined,
      model: str(s.model),
      q: str(s.q),
      waiting: (EQUIPMENT_WAITING as readonly string[]).includes(String(s.waiting))
        ? (s.waiting as EquipmentWaiting)
        : undefined,
      sort: (EQUIPMENT_SORTS as readonly string[]).includes(String(s.sort))
        ? (s.sort as EquipmentSort)
        : undefined,
      dir: s.dir === 'desc' ? 'desc' : s.dir === 'asc' ? 'asc' : undefined,
      page: page(s.page),
    }),
    component: EquipmentListPage,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/equipment/settings',
    component: EquipmentSettingsPage,
  }),
  createRoute({ getParentRoute: () => appRoute, path: '/equipment/$id', component: EquipmentPage }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/helpdesk',
    validateSearch: (s: Record<string, unknown>): HelpdeskSearch => ({
      scope:
        s.scope === 'all' || s.scope === 'free' || s.scope === 'created' || s.scope === 'mine'
          ? s.scope
          : undefined,
      state: s.state === 'overdue' || s.state === 'done' || s.state === 'all' ? s.state : undefined,
      q: str(s.q),
      page: page(s.page),
    }),
    component: TicketsPage,
  }),
  createRoute({ getParentRoute: () => appRoute, path: '/helpdesk/$id', component: TicketPage }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/safety',
    validateSearch: (s: Record<string, unknown>): SafetySearch => ({
      state: (['overdue', 'missing', 'soon', 'ok', 'exempt', 'all'] as const).find((v) => v === s.state),
      category: safetyCategory(s.category),
      department: str(s.department),
      q: str(s.q),
    }),
    component: SafetyStatusPage,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/safety/journal',
    validateSearch: (s: Record<string, unknown>): SafetyJournalSearch => ({
      category: safetyCategory(s.category),
      kind: str(s.kind),
      department: str(s.department),
      from: isoDay(s.from),
      to: isoDay(s.to),
      unsigned: flag(s.unsigned),
      q: str(s.q),
      page: page(s.page),
    }),
    component: SafetyJournalPage,
  }),
  createRoute({ getParentRoute: () => appRoute, path: '/safety/my', component: SafetyMyPage }),
  createRoute({ getParentRoute: () => appRoute, path: '/safety/settings', component: SafetySettingsPage }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/safety/employees/$id',
    component: SafetyEmployeePage,
  }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/meals',
    validateSearch: (s: Record<string, unknown>): MealsSearch => ({ date: isoDay(s.date) }),
    component: MealsPage,
  }),
  createRoute({ getParentRoute: () => appRoute, path: '/meals/my', component: MealsMyPage }),
  createRoute({ getParentRoute: () => appRoute, path: '/meals/people', component: MealsPeoplePage }),
  createRoute({
    getParentRoute: () => appRoute,
    path: '/meals/report',
    validateSearch: (s: Record<string, unknown>): MealsReportSearch => ({
      month: typeof s.month === 'string' && /^\d{4}-\d{2}$/.test(s.month) ? s.month : undefined,
    }),
    component: MealsReportPage,
  }),
  createRoute({ getParentRoute: () => appRoute, path: '/meals/settings', component: MealsSettingsPage }),
  createRoute({ getParentRoute: () => appRoute, path: '/notifications', component: NotificationsPage }),
  createRoute({ getParentRoute: () => appRoute, path: '/profile', component: ProfilePage }),
  createRoute({ getParentRoute: () => appRoute, path: '/admin/access', component: AdminAccessPage }),
  createRoute({ getParentRoute: () => appRoute, path: '/admin/routes', component: AdminRoutesPage }),
  createRoute({ getParentRoute: () => appRoute, path: '/admin/migration', component: AdminMigrationPage }),
  ...MODULES.filter((m) => m.status === 'planned').map((m) =>
    createRoute({ getParentRoute: () => appRoute, path: m.path, component: PlannedModulePage }),
  ),
];

const routeTree = rootRoute.addChildren([loginRoute, safetyPrintRoute, appRoute.addChildren(routes)]);

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
